import type { ICheckoutContext } from '@/types/context';
import { PaymentMethodEnum } from '@/types/common';
import {
  buildNuveiCardPaymentRequest,
  buildNuveiSaveCardPaymentRequest,
  type NuveiCardDetails,
  getNuveiCardToken,
} from '@/utils/nuvei';
import type { Nuvei3DSResponse, SafeChargeFields, SafeChargeField } from '@/types/nuvei';
import { Nuvei3DSErrorDescription } from '@/types/nuvei';
import { Errors } from '@/constants/errors';
import type { NuveiPaymentResponse } from '@/types/payment';

const isNotSupport3DSecureResponse = (response: Nuvei3DSResponse): boolean => {
  return (
    response.result === 'ERROR' &&
    response.errorDescription === Nuvei3DSErrorDescription.CARD_DOES_NOT_SUPPORT_3D_SECURE
  );
};

const is3DSecureResponse = (response: unknown): response is Nuvei3DSResponse => {
  return typeof response === 'object' && response !== null && 'result' in response && response.result === 'APPROVED';
};

const isError3DSecureResponse = (response: unknown): response is Nuvei3DSResponse => {
  return typeof response === 'object' && response !== null && 'result' in response && response.result === 'ERROR';
};

export const handleNuveiCardPayment = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: (params: unknown) => Promise<NuveiPaymentResponse>,
  params: Record<string, unknown>
): Promise<unknown> => {
  const paymentMethodSettings = checkout.paymentMethodsSettings.find(
    (pm) => pm.paymentMethodName === PaymentMethodEnum.CREDIT_CARD
  );

  if (!paymentMethodSettings) {
    throw new Error(Errors.payment.paymentFailed);
  }
  try {
    const selectedCard = checkout.card.selectedCard;
    if (selectedCard && !params.newCard) {
      const sessionResponse = await checkout.actions.createSession({
        paymentMethodId: paymentMethodSettings.id || '',
        email: checkout.customer.email || '',
      });
      const sessionToken = sessionResponse.sessionToken;

      // CVV is passed as 'cvc' from SavedCardsForm
      const cvc = (params.cvc as string) || '';

      return await sendPaymentToServer(
        buildNuveiSaveCardPaymentRequest({
          email: checkout.customer.email || '',
          paymentMethodId: paymentMethodSettings.id || '',
          sessionToken,
          cvc,
          pspIdentifier: selectedCard.pspCardIdentifier || '',
          cardholderName: selectedCard.cardholderName || '',
        })
      );
    }

    const safeChargeFields = checkout.card.cardElementRef?.current as SafeChargeFields;
    if (!safeChargeFields) {
      throw new Error(Errors.payment.nuveiDOMNotFound);
    }

    let sessionToken: string;
    if (safeChargeFields.sessionToken) {
      sessionToken = safeChargeFields.sessionToken;
    } else {
      const sessionResponse = await checkout.actions.createSession({
        paymentMethodId: paymentMethodSettings.id || '',
        email: checkout.customer.email || '',
      });
      sessionToken = sessionResponse.sessionToken;
      safeChargeFields.sessionToken = sessionToken;
    }

    const cardDetails = await new Promise<NuveiCardDetails>((resolve) => {
      safeChargeFields.safeChargeInstance?.getCardDetails(
        {
          sessionToken,
          useFields: true,
        },
        (response: unknown) => resolve(response as NuveiCardDetails)
      );
    });
    safeChargeFields.cardDetails = cardDetails;

    return new Promise((resolve, reject) => {
      safeChargeFields.safeChargeInstance?.authenticate3d(
        {
          sessionToken,
          cardHolderName: checkout.card.initialCardPayload?.cardholderName || '',
          paymentOption: safeChargeFields.cardObj,
          billingAddress: {
            email: checkout.customer.email ?? '',
            country: checkout.customer.geoLocator?.countryCode2 ?? '',
            address: checkout.addressDetails?.address ?? '',
            zip: checkout.addressDetails?.zipCode ?? '',
          },
        },
        async (authenticated3DResponse: unknown) => {
          if (isNotSupport3DSecureResponse(authenticated3DResponse as Nuvei3DSResponse)) {
            const tokenResponse = await getNuveiCardToken(
              safeChargeFields.safeChargeInstance!,
              safeChargeFields.cardObj as SafeChargeField,
              sessionToken,
              checkout.card.initialCardPayload?.cardholderName || '',
              checkout.customer.email ?? '',
              checkout.customer.geoLocator?.countryCode2 ?? ''
            );
            if (tokenResponse) {
              const results = (await sendPaymentToServer(
                buildNuveiCardPaymentRequest({
                  email: checkout.customer.email || '',
                  paymentMethodId: paymentMethodSettings.id || '',
                  sessionToken,
                  pspIdentifier: tokenResponse.ccTempToken,
                  shouldSaveCard: checkout.card.initialCardPayload?.isSaveCard ?? false,
                  cardData: cardDetails,
                  cardholderName: checkout.card.initialCardPayload?.cardholderName || '',
                })
              )) as NuveiPaymentResponse;
              resolve(results);
            } else {
              resolve(null);
            }
          }
          if (is3DSecureResponse(authenticated3DResponse)) {
            const ccTempToken = authenticated3DResponse.ccTempToken;
            const transactionId = authenticated3DResponse.transactionId;
            const results = (await sendPaymentToServer(
              buildNuveiCardPaymentRequest({
                email: checkout.customer.email || '',
                paymentMethodId: paymentMethodSettings.id || '',
                transactionId,
                sessionToken,
                pspIdentifier: ccTempToken,
                shouldSaveCard: checkout.card.initialCardPayload?.isSaveCard ?? false,
                cardData: cardDetails,
                cardholderName: checkout.card.initialCardPayload?.cardholderName || '',
              })
            )) as NuveiPaymentResponse;
            resolve(results);
          }
          if (isError3DSecureResponse(authenticated3DResponse)) {
            reject(new Error(authenticated3DResponse.errorDescription));
          }
        }
      );
    });
  } catch (error) {
    console.error(error);
    return null;
  }
};
